import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { ChangeEvent, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';

import {
    Button,
    Card,
    InfoCard,
    Input,
    Modal,
    Textarea,
    VerticalContainer,
} from '@/components';
import { AiNurse } from './ui/AiNurse';
import { useAiNurse } from './model/useAiNurse';
import {
    bookingDraftAtom,
    bookingErrorAtom,
    updateDraftAtom,
} from '@/features/booking/shared/model/bookingState';
import { useUrlState } from '@/features/booking/shared/model/useUrlState';
import { InputType, useStopWords } from './model/useStopwords';
import { R } from '@/features/booking/resources/Texts.resx';
import { CaregiverRoleEnum, ConsultationTypeEnum } from '@/shared/enums';
import { FormControl } from '@/caregiverSpace/shared/ui';
import { StopwordCard } from '@/features/booking/widgets/describe-problem/ui/StopwordCard';
import { AttachmentsDropzone } from '@/features/booking/widgets/describe-problem/ui/AttachmentsDropzone';
import { AiNurseStep } from '@/features/booking/shared/model/types';
import { Utils } from '@/shared/Utils';
import { useMutation } from '@tanstack/react-query';
import { Log } from '@/shared/Log';
import { patchPetWeight } from '@/features/booking/widgets/describe-problem/api/updatePetWeight';
import { ProductDto } from '@/shared/interfaces';

type DescribeProblemProps = {
    onSubmit: () => void;
    chosenProduct: ProductDto;
};

type FormData = {
    subject: string;
    message: string;
    petWeight: string;
};

export const DescribeProblem = ({
    onSubmit,
    chosenProduct,
}: DescribeProblemProps) => {
    const draftState = useAtomValue(bookingDraftAtom);
    const updateDraftState = useSetAtom(updateDraftAtom);
    const [_, setErrorMessage] = useAtom(bookingErrorAtom);
    const { urlState, updateUrl } = useUrlState();

    const [shouldResize, setShouldResize] = useState<boolean>(true);
    const [isUploadInProgress, setIsUploadInProgress] = useState(false);
    const isAttachmentsEnabled = chosenProduct.IsAttachmentsEnabled;
    const isAttachmentsRequired = chosenProduct.IsAttachmentsRequired;
    const isVideo = chosenProduct.IsVideo;
    const isPrescription =
        chosenProduct.ConsultationType === ConsultationTypeEnum.Prescription;
    const isVeterinarian =
        chosenProduct.HandledBy === CaregiverRoleEnum.Veterinarian;

    const aiNurse = useAiNurse(chosenProduct);

    const {
        register,
        handleSubmit,
        formState: { errors },
        reset,
        trigger,
    } = useForm<FormData>({
        mode: 'onBlur',
        defaultValues: {
            subject: draftState?.subject ?? '',
            message: draftState?.message ?? '',
            petWeight: draftState?.originalPetWeight ?? '',
        },
    });

    // set initial values for form fields from draft state when component mounts
    useEffect(() => {
        if (!draftState) return;
        reset({
            subject: draftState.subject ?? '',
            message: draftState.message ?? '',
            petWeight: draftState.originalPetWeight ?? '',
        });
    }, [
        draftState?.subject,
        draftState?.message,
        draftState?.originalPetWeight,
        reset,
    ]);

    // when skipping AI nurse, we continue to next step in booking flow and disable AI nurse for the remainder of this booking
    useEffect(() => {
        if (draftState?.aiNurseStep === AiNurseStep.Skipped) {
            updateDraftState({ aiNurseStep: AiNurseStep.Disabled });
            onSubmit();
        }

        if (draftState?.aiNurseStep === AiNurseStep.Completed) {
            setShouldResize(!shouldResize);
        }
    }, [draftState?.aiNurseStep]);

    const updatePetWeightMutation = useMutation({
        mutationFn: patchPetWeight,
        onSuccess: () => {
            updateDraftState({
                originalPetWeight: draftState?.editedPetWeight,
            });
        },
        onError: () => {
            Log.error('Failed to update pet weight');
        },
    });

    const submit = async (formData: FormData) => {
        const isValid = await trigger();
        if (!isValid) return;

        const attachmentUids = draftState?.attachmentUids;
        if (isAttachmentsRequired && !attachmentUids?.length) {
            setErrorMessage(R.AttachmentsRequiredErrorMessage);
            return;
        }

        updateDraftState({
            subject: formData.subject,
            message: formData.message,
        });

        if (isVeterinarian && urlState.petId) {
            updatePetWeightIfNeeded();
        }

        logStopWordsOnSubmit();

        if (aiNurse.isEnabled) {
            const wasNecessary = await aiNurse.openIfNecessary(
                formData.subject,
                formData.message,
            );
            if (wasNecessary) return;
            else updateDraftState({ aiNurseStep: AiNurseStep.Skipped });
        }

        onSubmit();
    };

    const updatePetWeightIfNeeded = () => {
        const weightWasEdited =
            draftState?.editedPetWeight &&
            draftState?.editedPetWeight != draftState?.originalPetWeight;
        if (!weightWasEdited) return;

        const petWeight = Utils.normalizeNumber(draftState.editedPetWeight);

        if (isNaN(petWeight) || petWeight <= 0) {
            setErrorMessage(R.PleaseEnterANumber);
            return;
        }

        updatePetWeightMutation.mutate({
            petId: parseInt(urlState.petId),
            petWeightKg: petWeight,
        });
    };

    const onUpdateAttachments = (
        attachmentUids: string[],
        uploadInProgress: boolean,
    ) => {
        setIsUploadInProgress(uploadInProgress);
        if (uploadInProgress) return; // we only update draft state when upload is done (to avoid temporary ids)
        updateDraftState({ attachmentUids: attachmentUids.join(',') });
    };

    const updateSubject = (e: React.FocusEvent<HTMLInputElement>) => {
        const value = e.target.value;
        updateDraftState({ subject: value });
    };

    const updateMessage = (e: React.FocusEvent<HTMLTextAreaElement>) => {
        const value = e.currentTarget.value;
        updateDraftState({ message: value });
    };

    const updatePetWeight = (e: React.FocusEvent<HTMLInputElement>) => {
        const value = e.target.value;
        updateDraftState({
            editedPetWeight: Utils.normalizeNumber(value).toString(),
        });
    };

    const handleSwitchToMessage = () => {
        const updatedUrlState = {
            ...urlState,
            consultationType:
                ConsultationTypeEnum[ConsultationTypeEnum.Message],
        };
        updateUrl(updatedUrlState, true);
    };

    const {
        stopwordMatchSubject,
        stopwordMatchMessage,
        showSubjectWarning,
        showMessageWarning,
        checkForStopwords,
        closeSubjectWarning,
        closeMessageWarning,
        logStopWordsOnSubmit,
    } = useStopWords({
        consultationType: chosenProduct.ConsultationType,
        isEmailConsultation: !chosenProduct.IsVideo,
    });

    const handleOnChange = (
        e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
        inputType: InputType,
    ) => {
        const value = e.target.value;
        checkForStopwords(value, inputType);
    };

    if (aiNurse.showAiNurse) {
        return <AiNurse aiNurse={aiNurse} onSubmitAndContinue={onSubmit} />;
    }

    return (
        <Card alwaysCard={false}>
            <h2 className="mb-6">{R.DescribeProblemHeader}</h2>

            <form onSubmit={handleSubmit(submit)}>
                <VerticalContainer spacing="md">
                    <FormControl
                        id="subject"
                        label={R.SubjectLabel}
                        error={errors.subject?.message}
                    >
                        <Input
                            placeholder={R.SubjectPlaceholder}
                            type="text"
                            maxLength={67}
                            {...register('subject', {
                                required: R.SubjectRequired,
                            })}
                            onFocus={() => {
                                if (draftState || errors) return;
                                updateDraftState({ subject: '' });
                            }}
                            onBlur={(e) => {
                                updateSubject(e);
                                register('subject').onBlur(e);
                            }}
                            onChange={(e) =>
                                handleOnChange(e, InputType.Subject)
                            }
                        />
                    </FormControl>

                    {showSubjectWarning && (
                        <StopwordCard
                            matchedWord={stopwordMatchSubject.matchedWord}
                            title={stopwordMatchSubject.title}
                            message={stopwordMatchSubject.message}
                            badgeText={stopwordMatchSubject.badgeText}
                            onClose={closeSubjectWarning}
                        />
                    )}

                    {!isVideo && (
                        <FormControl
                            id="message"
                            label={R.MessageLabel}
                            error={errors.message?.message}
                        >
                            <Textarea
                                rows={5}
                                placeholder={
                                    isPrescription ? '' : R.MessagePlaceholder
                                }
                                {...register('message', {
                                    required: R.MessageRequired,
                                })}
                                onFocus={() => {
                                    if (draftState) return;
                                    updateDraftState({ message: '' });
                                }}
                                onBlur={(e) => {
                                    updateMessage(e);
                                    register('message').onBlur(e);
                                }}
                                onChange={(e) =>
                                    handleOnChange(e, InputType.Message)
                                }
                                triggerResize={shouldResize}
                            />
                        </FormControl>
                    )}

                    {showMessageWarning && (
                        <StopwordCard
                            matchedWord={stopwordMatchMessage.matchedWord}
                            title={stopwordMatchMessage.title}
                            message={stopwordMatchMessage.message}
                            badgeText={stopwordMatchMessage.badgeText}
                            onClose={closeMessageWarning}
                        />
                    )}

                    {isPrescription && !showMessageWarning && (
                        <div className="text-text-primary text-sm whitespace-pre-wrap">
                            {R.PrescriptionQuestions}
                        </div>
                    )}

                    {isVeterinarian && (
                        <VerticalContainer spacing="sm">
                            <FormControl
                                label={R.CurrentPetWeight}
                                error={errors.petWeight?.message}
                            >
                                <Input
                                    placeholder={R.CurrentPetWeightPlaceholder}
                                    suffix={R.KilosSuffix}
                                    name={R.CurrentPetWeight}
                                    {...register('petWeight', {
                                        valueAsNumber: false,
                                        setValueAs: Utils.normalizeNumber,
                                        validate: (value) =>
                                            Utils.isNumberOrEmpty(
                                                value?.toString(),
                                            ) || R.PleaseEnterANumber,
                                    })}
                                    onBlur={(e) => {
                                        updatePetWeight(e);
                                        register('petWeight').onBlur(e);
                                    }}
                                />
                            </FormControl>

                            <div className="text-text-primary text-sm whitespace-pre-wrap">
                                {R.CurrentPetWeightHelperText}
                            </div>
                        </VerticalContainer>
                    )}

                    {isAttachmentsEnabled && (
                        <AttachmentsDropzone onChange={onUpdateAttachments} />
                    )}

                    {!!chosenProduct?.Texts?.ImportantInfo && (
                        <InfoCard className="rounded-2xl border border-gray-300">
                            <div className="text-primary mb-2 font-bold">
                                {R.ImportantInfoHeader}
                            </div>
                            <div
                                dangerouslySetInnerHTML={{
                                    __html: chosenProduct?.Texts?.ImportantInfo,
                                }}
                            />
                        </InfoCard>
                    )}

                    <div className="ns:justify-end flex w-full flex-row">
                        <Button
                            isSubmitting={
                                isUploadInProgress || aiNurse.isAskingAiNurse
                            }
                            variant="primary"
                            type="submit"
                            className="ns:w-auto mt-2 w-full min-w-44"
                        >
                            {R.Next}
                        </Button>
                    </div>
                </VerticalContainer>
            </form>

            {chosenProduct.ConsultationType === ConsultationTypeEnum.Video && (
                <div className="flex flex-col gap-2">
                    <span className="text-sm">
                        <button
                            className="text-action hover:text-action-dark cursor-pointer"
                            onClick={handleSwitchToMessage}
                        >
                            {R.YouCanAlsoMessageUs1}
                        </button>
                        &nbsp;{R.YouCanAlsoMessageUs2}
                    </span>
                </div>
            )}

            {aiNurse.showAiNurseModal && (
                <Modal
                    onClose={() => aiNurse.closeModal()}
                    hideCloseButton={true}
                >
                    <h2 className="-mt-4 mb-6">{R.AiNurseModalHeader}</h2>
                    <div className="mb-6 whitespace-pre-wrap">
                        {R.AiNurseModalDescription}
                    </div>
                    <div className="flex items-center justify-between">
                        <Button variant="ghost" onClick={aiNurse.skip}>
                            {R.Skip}
                        </Button>
                        <Button
                            variant="primary"
                            onClick={aiNurse.goToQuestion}
                        >
                            {R.GoToQuestion}
                        </Button>
                    </div>
                </Modal>
            )}
        </Card>
    );
};
